import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import strings from "../../i18n/language";
import { getAlertsAction } from "../../redux/actions/alert";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const ROW_PER_PAGE = 20;

const TYPE_FILTERS = [
  { value: null, label: strings.LABEL_ALERTS_FILTER_ALL },
  { value: "outside_hours", label: strings.LABEL_ALERTS_TYPE_OUTSIDE_HOURS },
  { value: "two_locals", label: strings.LABEL_ALERTS_TYPE_TWO_LOCALS },
];

const TYPE_META = {
  outside_hours: { bg: 'rgba(245,181,68,0.14)', color: '#f5b544' },
  two_locals: { bg: 'rgba(255,123,123,0.14)', color: '#ff7b7b' },
};

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

// Opening hours are decimal: 7.5 is 7:30
const formatHour = (value) => {
  const hours = Math.floor(value);
  const minutes = Math.round((value - hours) * 60);
  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const FilterChip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? 'rgba(59,149,232,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(59,149,232,0.5)' : BORDER}`,
    borderRadius: 8,
    color: active ? BRAND : MUTED,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    letterSpacing: 0.2,
  }}>
    {label}
  </button>
);

const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type] || { bg: GLASS, color: MUTED };
  const label = TYPE_FILTERS.find((f) => f.value === type)?.label || type;
  return (
    <span style={{
      background: meta.bg,
      color: meta.color,
      borderRadius: 6,
      padding: '3px 9px',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
};

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

const detailText = (alert) => {
  const details = alert.details || {};
  if (alert.type === "outside_hours") {
    const slots = details.slots || [];
    if (!slots.length) {
      return strings.formatString(strings.LABEL_ALERTS_DETAIL_CLOSED_DAY, details.localTime || '—');
    }
    const hours = slots.map((slot) => `${formatHour(slot.open)}–${formatHour(slot.close)}`).join(', ');
    return strings.formatString(strings.LABEL_ALERTS_DETAIL_OUTSIDE_HOURS, details.localTime || '—', hours);
  }
  if (alert.type === "two_locals") {
    return strings.formatString(
      strings.LABEL_ALERTS_DETAIL_TWO_LOCALS,
      details.minutes ?? '—',
      details.otherLocalLabel || strings.LABEL_ACTIVITIES_NO_LOCAL,
    );
  }
  return '—';
};

const NavButton = ({ disabled, onClick, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${BORDER}`,
      borderRadius: 8,
      color: disabled ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
      cursor: disabled ? 'not-allowed' : 'pointer',
      width: 32, height: 32,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {children}
  </button>
);

const AlertList = () => {
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState(null);
  const dispatch = useDispatch();

  const alerts = useSelector((state) => state.alert.alerts);
  const total = useSelector((state) => state.alert.total);
  const isLoading = useSelector((state) => state.alert.isLoadingGetAlerts);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);

  useEffect(() => {
    dispatch(getAlertsAction(ROW_PER_PAGE, page * ROW_PER_PAGE, typeFilter, selectedCompany?.id));
  }, [dispatch, page, typeFilter, selectedCompany]);

  const handleFilterChange = (value) => {
    setTypeFilter(value);
    setPage(0);
  };

  const totalPages = Math.ceil(total / ROW_PER_PAGE);

  return (
    <Box sx={{ width: "100%" }}>
      <div style={card}>

        {/* Header + Filters */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              {strings.LABEL_ALERTS_TITLE}
            </Typography>
            {total > 0 && (
              <span style={{ fontSize: 12, color: MUTED }}>
                {total} {strings.LABEL_ALERTS_TOTAL}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
            {strings.LABEL_ALERTS_SUBTITLE}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TYPE_FILTERS.map((f) => (
              <FilterChip
                key={f.value ?? 'all'}
                label={f.label}
                active={typeFilter === f.value}
                onClick={() => handleFilterChange(f.value)}
              />
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_ALERTS_DATE}</ColHeader>
                <ColHeader style={{ width: 130 }}>{strings.LABEL_ALERTS_TYPE}</ColHeader>
                <ColHeader>{strings.LABEL_ALERTS_CUSTOMER}</ColHeader>
                <ColHeader>{strings.LABEL_ALERTS_LOCAL}</ColHeader>
                <ColHeader style={{ width: 70 }}>{strings.LABEL_ALERTS_POINTS}</ColHeader>
                <ColHeader>{strings.LABEL_ALERTS_DETAIL}</ColHeader>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>
                    <CircularProgress size={24} sx={{ color: BRAND }} />
                  </td>
                </tr>
              )}
              {!isLoading && alerts.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 40, fontSize: 13, color: MUTED }}>
                    {strings.LABEL_ALERTS_EMPTY}
                  </td>
                </tr>
              )}
              {!isLoading && alerts.map((alert) => (
                <tr key={alert.id} style={{ transition: 'background 0.1s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Td style={{ color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {dayjs(alert.date).format("LLL")}
                  </Td>
                  <Td><TypeBadge type={alert.type} /></Td>
                  <Td>
                    <Tooltip title={alert.customer?.id || ''} placement="top">
                      <span>{alert.customer?.display_name || '—'}</span>
                    </Tooltip>
                  </Td>
                  <Td style={{ color: MUTED }}>
                    {alert.Address ? alert.Address.label : strings.LABEL_ACTIVITIES_NO_LOCAL}
                  </Td>
                  <Td style={{ fontWeight: 700, color: '#e8f0fe' }}>+{alert.points}</Td>
                  <Td>{detailText(alert)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 24px' }}>
            <span style={{ fontSize: 12, color: MUTED }}>
              {page + 1} / {totalPages}
            </span>
            <NavButton disabled={page === 0} onClick={() => setPage(page - 1)}>
              <NavigateBeforeRoundedIcon style={{ fontSize: 18 }} />
            </NavButton>
            <NavButton disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              <NavigateNextRoundedIcon style={{ fontSize: 18 }} />
            </NavButton>
          </div>
        )}
      </div>
    </Box>
  );
};

export default AlertList;
